import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ENUM_LABELS, formatMoney } from "@/modules/config";
import { useLang } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export function RecordDialog({ open, onOpenChange, config, record, onSaved }) {
  const { t } = useLang();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [terminals, setTerminals] = useState([]);
  const isEdit = Boolean(record);
  const isPos = config.custom === "pos";

  useEffect(() => {
    if (!open) return;
    const initial = {};
    config.fields.forEach((f) => {
      if (record && record[f.key] != null) initial[f.key] = record[f.key];
      else if (f.default != null) initial[f.key] = f.default;
      else initial[f.key] = "";
    });
    setForm(initial);
    if (isPos) {
      api.get("/finance/pos_terminals").then(({ data }) => setTerminals(data)).catch(() => {});
    }
  }, [open, record, config, isPos]);

  const setField = (key, value) => setForm((s) => ({ ...s, [key]: value }));

  const handleTerminalChange = (name) => {
    setForm((s) => {
      const t = terminals.find((x) => x.name === name);
      return { ...s, terminal: name, commission_rate: t ? t.commission_rate ?? 0 : s.commission_rate };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      config.fields.forEach((f) => {
        const v = form[f.key];
        if (v !== "" && v != null) payload[f.key] = v;
      });
      if (isEdit) {
        await api.patch(`/finance/${config.endpoint}/${record.id}`, payload);
        toast.success("Kayıt güncellendi");
      } else {
        await api.post(`/finance/${config.endpoint}`, payload);
        toast.success("Kayıt eklendi");
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const gross = parseFloat(form.gross_amount) || 0;
  const rate = parseFloat(form.commission_rate) || 0;
  const commissionAmt = Math.round(gross * rate) / 100;
  const netAmt = Math.round((gross - commissionAmt) * 100) / 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="record-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#0A1128]">
            {isEdit ? `${t(config.singular)} ${t("Düzenle")}` : `${t("Yeni")} ${t(config.singular)}`}
          </DialogTitle>
          {isPos && (
            <p className="text-sm text-slate-500">
              {t("Günsonu (brüt) tutar ve komisyon oranını girin — net tutar otomatik hesaplanır.")}
            </p>
          )}
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="record-form">
          {config.fields.map((f) => (
            <div key={f.key} className="space-y-2">
              <Label htmlFor={`field-${f.key}`}>
                {t(f.label)} {f.required && <span className="text-red-500">*</span>}
              </Label>
              {f.type === "enum" ? (
                <Select
                  value={form[f.key] ?? ""}
                  onValueChange={(v) => setField(f.key, v)}
                >
                  <SelectTrigger data-testid={`field-${f.key}`} className="focus:ring-blue-600">
                    <SelectValue placeholder={t("Seçiniz")} />
                  </SelectTrigger>
                  <SelectContent>
                    {f.options.map((o) => (
                      <SelectItem key={o} value={o} data-testid={`field-${f.key}-option-${o}`}>
                        {t(ENUM_LABELS[o] || o)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : f.type === "terminal_select" ? (
                terminals.length > 0 ? (
                  <Select value={form[f.key] ?? ""} onValueChange={handleTerminalChange}>
                    <SelectTrigger data-testid={`field-${f.key}`} className="focus:ring-blue-600">
                      <SelectValue placeholder={t("Terminal seçin")} />
                    </SelectTrigger>
                    <SelectContent>
                      {terminals.map((t) => (
                        <SelectItem key={t.id} value={t.name} data-testid={`field-terminal-option-${t.id}`}>
                          {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id={`field-${f.key}`}
                    required={f.required}
                    value={form[f.key] ?? ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    placeholder="Terminal adı (POS'lar sekmesinden terminal ekleyebilirsiniz)"
                    data-testid={`field-${f.key}`}
                    className="focus-visible:ring-blue-600"
                  />
                )
              ) : f.type === "textarea" ? (
                <Textarea
                  id={`field-${f.key}`}
                  value={form[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  rows={3}
                  data-testid={`field-${f.key}`}
                  className="focus-visible:ring-blue-600"
                />
              ) : (
                <Input
                  id={`field-${f.key}`}
                  type={f.type === "date" ? "date" : f.type === "money" || f.type === "int" ? "number" : "text"}
                  step={f.type === "money" ? "0.01" : undefined}
                  required={f.required}
                  value={form[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  data-testid={`field-${f.key}`}
                  className="focus-visible:ring-blue-600"
                />
              )}
              {isPos && f.key === "commission_rate" && (
                <div className="rounded-md bg-slate-50 border border-slate-200 p-4 space-y-1.5" data-testid="pos-computed-panel">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t("Komisyon Tutarı:")}</span>
                    <span className="font-semibold text-amber-600 tabular">{formatMoney(commissionAmt)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t("Hesabınıza Geçen (Net):")}</span>
                    <span className="font-semibold text-green-600 tabular">{formatMoney(netAmt)}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="record-cancel-button">
              {t("Vazgeç")}
            </Button>
            <Button type="submit" disabled={saving} data-testid="record-save-button"
              className="bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Kaydet")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
